import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { reverseGeocode } from "@/shared/services/location.service";

export interface LocationData {
    city: string;
    pincode: string;
    state?: string;
    country?: string;
}

interface LocationState {
    location: LocationData | null;
    isModalOpen: boolean;
    isDetecting: boolean;
    detectError: string;
    setLocation: (data: LocationData) => void;
    clearLocation: () => void;
    openModal: () => void;
    closeModal: () => void;
    detectLocation: () => void;
    clearDetectError: () => void;
}

/** Set when login/logout fires before persist has rehydrated from localStorage. */
let pendingPincodeClear = false;

export const useLocationStore = create<LocationState>()(
    persist(
        (set, get) => ({
            location: null,
            isModalOpen: false,
            isDetecting: false,
            detectError: '',

            setLocation: (data) => {
                set({ location: data, isModalOpen: false, detectError: '' });
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('locationChanged', { detail: data }));
                }
            },

            clearLocation: () => {
                set({ location: null });
            },

            openModal: () => set({ isModalOpen: true }),

            closeModal: () => set({ isModalOpen: false, detectError: '' }),

            clearDetectError: () => set({ detectError: '' }),

            detectLocation: () => {
                if (!navigator.geolocation) {
                    set({ detectError: 'Geolocation is not supported' });
                    return;
                }

                set({ isDetecting: true, detectError: '' });

                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        try {
                            const data = await reverseGeocode(position.coords.latitude, position.coords.longitude);
                            const addr = data.address;

                            get().setLocation({
                                city: addr.city || addr.town || addr.village || addr.suburb || 'Unknown',
                                pincode: addr.postcode || '',
                                state: addr.state || '',
                                country: addr.country || '',
                            });

                            set({ isDetecting: false });
                        } catch {
                            set({
                                detectError: 'Failed to detect location. Enter pincode manually.',
                                isDetecting: false
                            });
                        }
                    },
                    () => {
                        set({
                            isDetecting: false,
                            detectError: 'Location permission denied.'
                        });
                    },
                    { timeout: 10000 }
                );
            },
        }),
        {
            name: 'location-storage',
            partialize: (state) => ({
                location: state.location,
            }),
            onRehydrateStorage: () => () => {
                if (pendingPincodeClear) {
                    pendingPincodeClear = false;
                    useLocationStore.getState().clearLocation();
                }
            },
        }
    )
);

function clearPincodeOnAuthChange() {
    pendingPincodeClear = true;
    useLocationStore.getState().clearLocation();
}

if (typeof window !== 'undefined') {
    window.addEventListener('userLogin', clearPincodeOnAuthChange);
    window.addEventListener('userLogout', clearPincodeOnAuthChange);
}